import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHeader from "../components/PageHeader";
import useEditableContent from "../hooks/useEditableContent";
import internationalProgrammes from "../data/internationalProgrammes";

const FALLBACK_PROGRAMMES = [];

const packageCopy = {
  ar: {
    international: "الرحلات الخارجية",
    directFlight: "رحلة مباشرة",
    days: "أيام",
    day: "يوم",
    from: "ابتداء من",
    details: "عرض التفاصيل",
    book: "احجز الآن",
    makkah: "مكة",
    madinah: "المدينة",
    separator: "، ",
    internationalFeatures: null,
  },
  fr: {
    international: "Voyages internationaux",
    directFlight: "Vol direct",
    days: "jours",
    day: "jour",
    from: "À partir de",
    details: "Voir détails",
    book: "Réserver",
    makkah: "Makkah",
    madinah: "Madinah",
    separator: ", ",
    internationalFeatures: ["Vols et hôtels inclus", "Petit-déjeuner selon le programme", "Excursions et accompagnement"],
  },
  en: {
    international: "International Trips",
    directFlight: "Direct Flight",
    days: "days",
    day: "day",
    from: "From",
    details: "View Details",
    book: "Book Now",
    makkah: "Makkah",
    madinah: "Madinah",
    separator: ", ",
    internationalFeatures: ["Flights and hotels included", "Breakfast as per program", "Tours and assistance"],
  },
};

const cardCopy = {
  101: {
    fr: { title: "Vietnam + Thaïlande", route: "Hanoï → Da Nang → Bangkok", badge: "Asie", dates: "20/06 au 05/07" },
    en: { title: "Vietnam + Thailand", route: "Hanoi → Da Nang → Bangkok", badge: "Asia", dates: "20/06 to 05/07" },
  },
  102: {
    fr: { title: "Thaïlande et îles Phi Phi", route: "Bangkok → Phuket → Îles Phi Phi", badge: "Îles et plages", dates: "Dates mensuelles d'avril à octobre" },
    en: { title: "Thailand and Phi Phi Islands", route: "Bangkok → Phuket → Phi Phi Islands", badge: "Islands and Beaches", dates: "Monthly dates from April to October" },
  },
  103: {
    fr: { title: "Kuala Lumpur + Bali", route: "Kuala Lumpur → Bali → Kuala Lumpur", badge: "Malaisie et Bali", dates: "Deux départs par mois d'avril à novembre 2026" },
    en: { title: "Kuala Lumpur + Bali", route: "Kuala Lumpur → Bali → Kuala Lumpur", badge: "Malaysia and Bali", dates: "Two monthly departures from April to November 2026" },
  },
  104: {
    fr: { title: "Maldives + Sri Lanka + Istanbul", route: "Maldives → Sri Lanka → Istanbul", badge: "Hôtels 5 étoiles", dates: "30/04 au 10/05" },
    en: { title: "Maldives + Sri Lanka + Istanbul", route: "Maldives → Sri Lanka → Istanbul", badge: "5-Star Hotels", dates: "30/04 to 10/05" },
  },
  105: {
    fr: { title: "Istanbul", route: "Casablanca → Istanbul → Casablanca", badge: "3 sorties gratuites", dates: "Plusieurs dates d'avril à juillet" },
    en: { title: "Istanbul", route: "Casablanca → Istanbul → Casablanca", badge: "3 Free Tours", dates: "Several dates from April to July" },
  },
  106: {
    fr: { title: "Égypte: Le Caire + Alexandrie + Charm el-Cheikh", route: "Le Caire → Alexandrie → Charm el-Cheikh", badge: "Égypte", dates: "Juillet et août" },
    en: { title: "Egypt: Cairo + Alexandria + Sharm El Sheikh", route: "Cairo → Alexandria → Sharm El Sheikh", badge: "Egypt", dates: "July and August" },
  },
};

const PackagesPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = ["ar", "fr", "en"].includes((i18n.language || "ar").split("-")[0]) ? (i18n.language || "ar").split("-")[0] : "ar";
  const copy = packageCopy[lang] || packageCopy.ar;
  const [filter, setFilter] = useState("all");
  const editableProgrammes = useEditableContent("programmes.json", FALLBACK_PROGRAMMES);
  const localProgrammes = Array.isArray(editableProgrammes) ? editableProgrammes : FALLBACK_PROGRAMMES;
  const programmes = [...localProgrammes, ...internationalProgrammes];
  const filtered = filter === "all" ? programmes : programmes.filter((p) => p.cat === filter);

  const filterBtns = [
    ["all", t("nav.packages")],
    ["umrah", t("search.tabs.omrah")],
    ["international", copy.international],
  ];

  return (
    <>
      <style>{`
        .pkgs-section { padding:64px 56px 88px; background:linear-gradient(180deg,#fbf8f1 0%,#f6efe3 100%); }
        .filter-bar { display:flex; gap:10px; justify-content:center; flex-wrap:wrap; margin-bottom:42px; }
        .filter-btn { min-height:40px; padding:9px 20px; border-radius:999px; border:1px solid rgba(13,72,51,0.14); font-size:13px; font-weight:700; cursor:pointer; transition:all .2s ease; font-family:var(--font-ui); background:#fff; color:#66756e; box-shadow:0 8px 22px rgba(13,27,42,.04); }
        .filter-btn.active { background:#0d4833; color:#fff; border-color:#0d4833; box-shadow:0 12px 28px rgba(13,72,51,.18); }
        .filter-btn:hover:not(.active) { border-color:var(--gold); color:#0d4833; transform:translateY(-1px); }
        .pkgs-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:26px; max-width:1180px; margin:0 auto; align-items:stretch; }
        .umrah-card { background:#fff; border:1px solid rgba(13,72,51,.08); border-radius:18px; overflow:hidden; box-shadow:0 14px 38px rgba(13,27,42,.08); transition:transform .28s ease, box-shadow .28s ease, border-color .28s ease; display:flex; flex-direction:column; min-height:100%; }
        .umrah-card:hover { transform:translateY(-6px); box-shadow:0 24px 58px rgba(13,27,42,.14); border-color:rgba(201,168,76,.36); }
        .umrah-media { position:relative; height:218px; aspect-ratio:16/10; overflow:hidden; background:#e9dfcc; }
        .umrah-media img { width:100%; height:100%; object-fit:cover; display:block; transition:transform .55s ease; }
        .umrah-card:hover .umrah-media img { transform:scale(1.045); }
        .umrah-media::after { content:""; position:absolute; inset:0; background:linear-gradient(180deg,rgba(0,0,0,.04),rgba(0,0,0,.42)); pointer-events:none; }
        .umrah-badge { position:absolute; top:14px; inset-inline-start:14px; z-index:1; background:rgba(255,255,255,.94); color:#0d4833; border:1px solid rgba(255,255,255,.5); padding:6px 11px; border-radius:999px; font-size:11px; font-weight:800; box-shadow:0 8px 18px rgba(0,0,0,.12); }
        .flight-badge { position:absolute; top:14px; inset-inline-end:14px; z-index:1; background:rgba(13,72,51,.92); color:#fff; border:1px solid rgba(232,201,122,.45); padding:6px 10px; border-radius:999px; font-size:11px; font-weight:800; box-shadow:0 8px 18px rgba(0,0,0,.12); }
        .price-badge { position:absolute; bottom:14px; inset-inline-start:14px; z-index:1; background:#0d4833; color:#fff; border:1px solid rgba(232,201,122,.45); padding:9px 13px; border-radius:12px; box-shadow:0 12px 24px rgba(0,0,0,.18); }
        .price-badge span { display:block; font-size:10px; color:#e8c97a; font-weight:800; line-height:1; margin-bottom:4px; }
        .price-badge strong { font-family:var(--font-heading); font-size:22px; line-height:1; letter-spacing:0; direction:ltr; unicode-bidi:isolate; display:block; }
        .umrah-body { padding:22px; display:flex; flex-direction:column; flex:1; }
        .umrah-title { font-size:23px; line-height:1.45; color:#0d1b2a; margin:0 0 7px; min-height:0; }
        .compact-meta { display:flex; justify-content:space-between; align-items:center; gap:12px; color:#7a817d; font-size:14px; line-height:1.6; margin-bottom:14px; }
        .compact-meta-item { display:inline-flex; align-items:center; gap:7px; min-width:0; }
        .compact-icon { color:#0d4833; font-size:15px; line-height:1; }
        .compact-location { color:#66716d; font-size:14px; line-height:1.8; margin:0 0 16px; padding-bottom:15px; border-bottom:1px solid #f0eadf; }
        .compact-location .pin { color:#b89131; margin-inline-end:6px; }
        .services-list { display:grid; gap:9px; margin-bottom:20px; }
        .service-item { display:flex; gap:9px; color:#46554f; font-size:13px; line-height:1.55; }
        .service-check { color:#0d7a4f; font-weight:900; flex:0 0 auto; }
        .card-actions { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-top:auto; }
        .card-actions button { min-height:42px; border-radius:10px; font-family:var(--font-ui); font-weight:800; cursor:pointer; transition:all .2s ease; }
        .details-btn { background:#fff; color:#0d4833; border:1px solid rgba(13,72,51,.18); }
        .details-btn:hover { border-color:var(--gold); color:var(--gold-dark); background:#fffaf0; }
        .book-btn { background:linear-gradient(135deg,#c9a84c,#a07830); color:#fff; border:0; box-shadow:0 12px 24px rgba(160,120,48,.2); }
        .book-btn:hover { transform:translateY(-1px); box-shadow:0 16px 30px rgba(160,120,48,.28); }
        @media(max-width:1020px){ .pkgs-grid{grid-template-columns:repeat(2,minmax(0,1fr));} }
        @media(max-width:720px){ .pkgs-section{padding:44px 18px 64px;} .filter-bar{justify-content:flex-start;} .pkgs-grid{grid-template-columns:1fr; gap:18px;} .umrah-media{height:210px;} .umrah-title{font-size:21px; min-height:auto;} .card-actions{grid-template-columns:1fr;} }
      `}</style>

      <PageHeader tag={t("nav.packages")} title={t("nav.packages")} subtitle={t("packagesPage.subtitle")} />

      <section className="pkgs-section">
        <div className="filter-bar">
          {filterBtns.map(([id, label]) => (
            <button key={id} className={`filter-btn ${filter === id ? "active" : ""}`} onClick={() => setFilter(id)}>
              {label}
            </button>
          ))}
        </div>

        <div className="pkgs-grid">
          {filtered.map((p) => {
            const services = (p.services || []).slice(0, 7);
            const localized = cardCopy[p.id]?.[lang] || {};
            const title = localized.title || p.title;
            const route = localized.route || p.route || p.location;
            const dates = localized.dates || p.dates;
            const badge = p.cat === "international" ? (localized.badge || p.badge || p.type) : (p.hotelLevel || p.badge || p.type);
            return (
              <article key={p.id} className="umrah-card">
                <div className="umrah-media">
                  <img src={p.img} alt={title || `Package ${p.id}`} loading="lazy" />
                  <div className="umrah-badge">{badge}</div>
                  <div className="flight-badge">{p.cat === "umrah" ? copy.directFlight : `${p.days} ${copy.day}`}</div>
                  <div className="price-badge">
                    <span>{copy.from}</span>
                    <strong>{p.price} DH</strong>
                  </div>
                </div>

                <div className="umrah-body">
                  <div className="compact-meta">
                    <span className="compact-meta-item"><span className="compact-icon">◷</span>{p.days} {copy.days}</span>
                    <span className="compact-meta-item"><span className="compact-icon">✈</span>{p.cat === "umrah" ? p.people : dates}</span>
                  </div>

                  <h3 className="umrah-title">{title}</h3>
                  <div className="compact-location">
                    <span className="pin">●</span>
                    {p.cat === "umrah" ? `${copy.makkah}: ${p.makkahHotel}${copy.separator}${copy.madinah}: ${p.madinahHotel}` : route}
                  </div>

                  <div className="services-list">
                    {(p.cat === "international" ? (copy.internationalFeatures || p.includes || []) : services).slice(0, 3).map((service, idx) => (
                      <div key={idx} className="service-item">
                        <span className="service-check">✓</span>
                        <span>{service}</span>
                      </div>
                    ))}
                  </div>

                  <div className="card-actions">
                    <button className="details-btn" onClick={() => navigate(`/packages/${p.id}`)}>{copy.details}</button>
                    <button className="book-btn" onClick={() => navigate("/contact")}>{copy.book}</button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </>
  );
};

export default PackagesPage;
